import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { API } from "@/lib/api";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from "recharts";
import { WarningCircle, ShieldWarning, TrendDown, Gear, FloppyDisk, ArrowUp, ArrowDown, Wallet, ChartLineUp, FilePdf, FileXls } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", minimumFractionDigits: 0 }).format(n || 0);

const downloadFile = (url, token) => {
  const a = document.createElement("a");
  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
    .then(r => r.blob())
    .then(blob => { a.href = URL.createObjectURL(blob); a.download = ""; a.click(); });
};

export function FinancialReportsPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("period");

  const subTabs = [
    { id: "period", label: "Resumen por Periodo" },
    { id: "alerts", label: "Alertas" },
    { id: "balance", label: "Saldo Acumulado" },
    ...(isAdmin ? [{ id: "config", label: "Configuracion" }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="financial-reports-panel">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {subTabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`report-subtab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "period" && <PeriodReport year={year} />}
      {tab === "alerts" && <AlertsPanel year={year} month={month} />}
      {tab === "balance" && <BalanceSummary year={year} />}
      {tab === "config" && isAdmin && <AlertConfig />}
    </div>
  );
}

/* ========== PERIOD REPORT ========== */
function PeriodReport({ year }) {
  const { token } = useAuth();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [period, setPeriod] = useState("monthly");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/admin/financial-report`, { params: { year, period }, headers: { Authorization: `Bearer ${token}` } });
      setData(d);
    } catch { toast.error("Error cargando reporte"); }
    setLoading(false);
  }, [year, period, token]);

  useEffect(() => { load(); }, [load]);

  const periods = [
    { id: "weekly", label: "Semanal" },
    { id: "biweekly", label: "Quincenal" },
    { id: "monthly", label: "Mensual" },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex gap-1 bg-white border p-0.5 rounded-lg">
            {periods.map(p => (
              <button key={p.id} onClick={() => setPeriod(p.id)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${period === p.id ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"}`}
                data-testid={`period-btn-${p.id}`}>
                {p.label}
              </button>
            ))}
          </div>
          {isAdmin && <div className="flex gap-1">
            <button onClick={() => downloadFile(`${API}/admin/financial-report/pdf?year=${year}&period=${period}`, token)} className="px-2.5 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="report-pdf-btn">
              <FilePdf size={14} /> PDF
            </button>
            <button onClick={() => downloadFile(`${API}/admin/financial-report/excel?year=${year}&period=${period}`, token)} className="px-2.5 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="report-excel-btn">
              <FileXls size={14} /> Excel
            </button>
          </div>}
        </div>
        {data?.totals && (
          <div className="flex gap-2 flex-wrap">
            <span className="text-xs font-semibold bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg" data-testid="total-income">Ingresos: {fmt(data.totals.income)}</span>
            {isAdmin && <span className="text-xs font-semibold bg-red-50 text-red-700 px-3 py-1.5 rounded-lg" data-testid="total-expense">Gastos: {fmt(data.totals.expense)}</span>}
            {isAdmin && <span className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${data.totals.profit >= 0 ? "bg-blue-50 text-blue-700" : "bg-red-50 text-red-700"}`} data-testid="total-profit">Utilidad: {fmt(data.totals.profit)}</span>}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : data?.rows && (
        <>
          {/* Chart */}
          <div className="bg-white rounded-xl border p-4">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={data.rows} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="period_label" tick={{ fontSize: 9 }} interval={period === "weekly" ? 3 : 0} angle={period === "weekly" ? -45 : 0} textAnchor={period === "weekly" ? "end" : "middle"} height={period === "weekly" ? 70 : 30} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => fmt(v)} />
                <Legend />
                <Bar dataKey="income" name="Ingresos" fill="#10B981" radius={[3, 3, 0, 0]} />
                {isAdmin && <Bar dataKey="expense" name="Gastos" fill="#EF4444" radius={[3, 3, 0, 0]} />}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Periodo</th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Ingresos</th>
                  <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Var.</th>
                  {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Gastos</th>}
                  {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Utilidad</th>}
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.rows.filter(r => r.income > 0 || (r.expense && r.expense > 0)).map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50" data-testid={`period-row-${i}`}>
                    <td className="px-3 py-2.5 font-medium text-slate-700">{r.period_label}</td>
                    <td className="px-3 py-2.5 text-right text-emerald-700 font-medium">{fmt(r.income)}</td>
                    <td className="px-3 py-2.5 text-center">
                      {r.income_change !== 0 && (
                        <span className={`inline-flex items-center gap-0.5 text-xs font-semibold ${r.income_change > 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {r.income_change > 0 ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                          {Math.abs(r.income_change)}%
                        </span>
                      )}
                    </td>
                    {isAdmin && <td className="px-3 py-2.5 text-right text-red-600 font-medium">{fmt(r.expense)}</td>}
                    {isAdmin && (
                      <td className={`px-3 py-2.5 text-right font-bold ${r.profit >= 0 ? "text-blue-700" : "text-red-700"}`}>
                        {fmt(r.profit)}
                      </td>
                    )}
                  </tr>
                ))}
                {data.rows.filter(r => r.income > 0 || (r.expense && r.expense > 0)).length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Sin datos para este periodo</td></tr>
                )}
              </tbody>
              {data.totals && (
                <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                  <tr className="font-bold">
                    <td className="px-3 py-3 text-slate-700">TOTAL</td>
                    <td className="px-3 py-3 text-right text-emerald-700">{fmt(data.totals.income)}</td>
                    <td></td>
                    {isAdmin && <td className="px-3 py-3 text-right text-red-700">{fmt(data.totals.expense)}</td>}
                    {isAdmin && <td className={`px-3 py-3 text-right ${data.totals.profit >= 0 ? "text-blue-700" : "text-red-700"}`}>{fmt(data.totals.profit)}</td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ========== ALERTS PANEL ========== */
function AlertsPanel({ year, month }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const selectedMonth = month || new Date().getMonth() + 1;
  const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/admin/alerts`, { params: { year, month: selectedMonth }, headers: { Authorization: `Bearer ${token}` } });
        setAlerts(data);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
      setLoading(false);
    };
    load();
  }, [year, selectedMonth, token]);

  const severityStyles = {
    danger: { bg: "bg-red-50 border-red-200", icon: "text-red-500", text: "text-red-800", badge: "bg-red-100 text-red-700" },
    warning: { bg: "bg-amber-50 border-amber-200", icon: "text-amber-500", text: "text-amber-800", badge: "bg-amber-100 text-amber-700" },
  };

  const iconMap = {
    expense_vs_income: ShieldWarning,
    category_growth: TrendDown,
    low_balance: Wallet,
    declining_trend: ChartLineUp,
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <WarningCircle size={20} className="text-slate-500" />
        <span className="text-sm font-medium text-slate-600">Alertas para {MONTHS[selectedMonth - 1]} {year}</span>
      </div>

      {alerts.length === 0 ? (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 text-center" data-testid="no-alerts">
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <p className="text-sm font-semibold text-emerald-700">Sin alertas activas</p>
          <p className="text-xs text-emerald-600 mt-1">Los indicadores financieros se encuentran dentro de los parametros normales</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a, i) => {
            const s = severityStyles[a.severity] || severityStyles.warning;
            const Icon = iconMap[a.type] || WarningCircle;
            return (
              <div key={i} className={`rounded-xl border p-4 ${s.bg}`} data-testid={`alert-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`mt-0.5 ${s.icon}`}><Icon size={22} weight="duotone" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`text-sm font-bold ${s.text}`}>{a.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.badge}`}>
                        {a.severity === "danger" ? "Critico" : "Advertencia"}
                      </span>
                    </div>
                    <p className={`text-sm ${s.text} opacity-80`}>{a.message}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isAdmin && (
        <p className="text-xs text-slate-400 text-center">Las alertas de gastos y utilidad solo estan disponibles para administradores</p>
      )}
    </div>
  );
}

/* ========== BALANCE SUMMARY ========== */
function BalanceSummary({ year }) {
  const { token, user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data: d } = await axios.get(`${API}/admin/balance-summary`, { params: { year }, headers: { Authorization: `Bearer ${token}` } });
        setData(d);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
      setLoading(false);
    };
    load();
  }, [year, token]);

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  const hasData = data.some(r => r.income > 0 || (r.expense && r.expense > 0));
  const currentBalance = data.length > 0 ? data[data.length - 1].accumulated : 0;
  const lastWithData = [...data].reverse().find(r => r.income > 0 || (r.expense && r.expense > 0));
  const displayBalance = lastWithData ? lastWithData.accumulated : 0;

  // Check for declining trend
  const dataWithActivity = data.filter(r => r.income > 0 || (r.expense && r.expense > 0));
  let decliningMonths = 0;
  for (let i = dataWithActivity.length - 1; i > 0; i--) {
    if (dataWithActivity[i].accumulated < dataWithActivity[i - 1].accumulated) decliningMonths++;
    else break;
  }

  return (
    <div className="space-y-4">
      {/* Export buttons */}
      {isAdmin && <div className="flex gap-2">
        <button onClick={() => downloadFile(`${API}/admin/balance-summary/pdf?year=${year}`, token)} className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-xs font-medium hover:bg-red-100 flex items-center gap-1" data-testid="balance-pdf-btn">
          <FilePdf size={14} /> Exportar PDF
        </button>
        <button onClick={() => downloadFile(`${API}/admin/balance-summary/excel?year=${year}`, token)} className="px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-lg text-xs font-medium hover:bg-emerald-100 flex items-center gap-1" data-testid="balance-excel-btn">
          <FileXls size={14} /> Exportar Excel
        </button>
      </div>}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className={`rounded-xl p-4 ${displayBalance >= 0 ? "bg-emerald-50" : "bg-red-50"}`} data-testid="balance-card">
          <p className="text-xs font-medium text-slate-500 mb-1">Saldo Acumulado {year}</p>
          <p className={`text-xl font-bold ${displayBalance >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(displayBalance)}</p>
        </div>
        <div className="rounded-xl p-4 bg-blue-50" data-testid="total-income-card">
          <p className="text-xs font-medium text-slate-500 mb-1">Total Ingresos</p>
          <p className="text-xl font-bold text-blue-700">{fmt(data.reduce((s, r) => s + r.income, 0))}</p>
        </div>
        {isAdmin && (
          <div className="rounded-xl p-4 bg-orange-50" data-testid="total-expense-card">
            <p className="text-xs font-medium text-slate-500 mb-1">Total Gastos</p>
            <p className="text-xl font-bold text-orange-700">{fmt(data.reduce((s, r) => s + (r.expense || 0), 0))}</p>
          </div>
        )}
      </div>

      {/* Declining warning */}
      {decliningMonths >= 2 && isAdmin && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3" data-testid="declining-warning">
          <TrendDown size={20} className="text-red-500" weight="duotone" />
          <p className="text-sm text-red-700 font-medium">El saldo acumulado ha disminuido durante {decliningMonths} meses consecutivos</p>
        </div>
      )}

      {/* Chart */}
      {hasData && (
        <div className="bg-white rounded-xl border p-4">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Saldo Acumulado Mensual</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="month_name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmt(v)} />
              <Legend />
              <ReferenceLine y={0} stroke="#EF4444" strokeDasharray="3 3" strokeWidth={1.5} />
              <Line type="monotone" dataKey="accumulated" name="Saldo Acumulado" stroke="#3B82F6" strokeWidth={3} dot={{ r: 4, fill: "#3B82F6" }} />
              <Line type="monotone" dataKey="income" name="Ingresos" stroke="#10B981" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />
              {isAdmin && <Line type="monotone" dataKey="expense" name="Gastos" stroke="#EF4444" strokeWidth={1.5} strokeDasharray="4 4" dot={false} />}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500">Mes</th>
              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Ingresos</th>
              {isAdmin && <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Gastos</th>}
              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Saldo Mes</th>
              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500">Acumulado</th>
              <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500">Tendencia</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.map((r, i) => {
              const prevAcc = i > 0 ? data[i - 1].accumulated : 0;
              const trend = r.accumulated - prevAcc;
              return (
                <tr key={i} className={`hover:bg-slate-50 ${r.income === 0 && (!r.expense || r.expense === 0) ? "opacity-40" : ""}`} data-testid={`balance-row-${i}`}>
                  <td className="px-3 py-2.5 font-medium text-slate-700">{r.month_name}</td>
                  <td className="px-3 py-2.5 text-right text-emerald-700 font-medium">{fmt(r.income)}</td>
                  {isAdmin && <td className="px-3 py-2.5 text-right text-red-600 font-medium">{fmt(r.expense)}</td>}
                  <td className={`px-3 py-2.5 text-right font-medium ${r.month_balance >= 0 ? "text-blue-700" : "text-red-700"}`}>{fmt(r.month_balance)}</td>
                  <td className={`px-3 py-2.5 text-right font-bold ${r.accumulated >= 0 ? "text-slate-900" : "text-red-700"}`}>{fmt(r.accumulated)}</td>
                  <td className="px-3 py-2.5 text-center">
                    {(r.income > 0 || (r.expense && r.expense > 0)) && (
                      trend > 0 ? <ArrowUp size={14} className="inline text-emerald-500" weight="bold" /> :
                      trend < 0 ? <ArrowDown size={14} className="inline text-red-500" weight="bold" /> :
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ========== ALERT CONFIG ========== */
function AlertConfig() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/alert-config`, { headers: { Authorization: `Bearer ${token}` } });
        setConfig(data);
      } catch (e) { process.env.NODE_ENV === "development" && console.error(e.message); }
      setLoading(false);
    };
    load();
  }, [token]);

  const save = async () => {
    try {
      await axios.put(`${API}/admin/alert-config`, config, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Configuracion de alertas guardada");
    } catch { toast.error("Error al guardar"); }
  };

  if (loading || !config) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="bg-white rounded-xl border p-6 max-w-xl space-y-5" data-testid="alert-config">
      <h3 className="font-semibold text-slate-700 flex items-center gap-2"><Gear size={18} /> Configuracion de Alertas</h3>
      <p className="text-xs text-slate-400">Define los umbrales para activar alertas financieras automaticas</p>

      <div className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-500">Alerta si gastos superan X% de ingresos</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="number" value={config.expense_income_pct} onChange={e => setConfig(p => ({ ...p, expense_income_pct: parseFloat(e.target.value) || 0 }))}
              className="w-24 px-3 py-2 border rounded-lg text-sm text-center font-bold" data-testid="cfg-expense-pct" />
            <span className="text-sm text-slate-500">% de los ingresos</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Ej: Si pones 80, se alerta cuando gastos sean 80% o mas de los ingresos del mes</p>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">Alerta si una categoria de gasto crece mas de X%</label>
          <div className="flex items-center gap-2 mt-1">
            <input type="number" value={config.category_growth_pct} onChange={e => setConfig(p => ({ ...p, category_growth_pct: parseFloat(e.target.value) || 0 }))}
              className="w-24 px-3 py-2 border rounded-lg text-sm text-center font-bold" data-testid="cfg-category-pct" />
            <span className="text-sm text-slate-500">% vs mes anterior</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Compara el gasto de cada categoria con el mes anterior</p>
        </div>

        <div>
          <label className="text-xs font-medium text-slate-500">Alerta si saldo acumulado cae debajo de</label>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-slate-500">$</span>
            <input type="number" value={config.min_balance} onChange={e => setConfig(p => ({ ...p, min_balance: parseFloat(e.target.value) || 0 }))}
              className="w-36 px-3 py-2 border rounded-lg text-sm text-center font-bold" data-testid="cfg-min-balance" />
            <span className="text-sm text-slate-500">MXN</span>
          </div>
          <p className="text-[10px] text-slate-400 mt-1">Umbral minimo de saldo acumulado anual</p>
        </div>
      </div>

      <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-1.5" data-testid="save-alert-config">
        <FloppyDisk size={16} /> Guardar Configuracion
      </button>
    </div>
  );
}
